import React, { useState, useEffect } from "react";
import {
  FaHourglassHalf,
  FaCheckCircle,
  FaTimesCircle,
  FaEye,
  FaSearch,
  FaCalendar,
  FaClock,
  FaUser,
  FaMoneyBillWave,
  FaMapMarkerAlt,
  FaDownload,
  FaFilter,
  FaChartBar,
  FaAd,
  FaUsers,
  FaPlay,
  FaStop,
  FaCheck,
  FaTimes,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AdminAdvertisementStatus = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [advertisements, setAdvertisements] = useState([]);
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const [runningAds, setRunningAds] = useState([]);
  // const [selectedAd, setSelectedAd] = useState(null);
  const [stats, setStats] = useState({ pending: 0, approved: 0, accepted: 0, rejected: 0, completed: 0 });
  const [runningStats, setRunningStats] = useState({ running: 0 });

  // Search and filter states
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [userTypeFilter, setUserTypeFilter] = useState("all");

  useEffect(() => {
    const fetchAdvertisements = async () => {
      try {
        setLoading(true);

        // Call both APIs in parallel
        const allAdsResponse = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/admin/get/all/ads`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        console.log("Fetched Advertisements Data:", allAdsResponse);


        // Handle data shape differences
        const allAdsData = allAdsResponse.data.data || allAdsResponse.data;

        setAdvertisements(allAdsData || []);

        setStats({
          pending: allAdsData.filter((ad) => ad.status === "pending").length,
          approved: allAdsData.filter((ad) => ad.status === "approved").length,
          accepted: allAdsData.filter((ad) => ad.status === "accepted").length,
          rejected: allAdsData.filter((ad) => ad.status === "rejected").length,
          completed: allAdsData.filter((ad) => ad.status === "completed").length,
        });
        setLoading(false);
      } catch (error) {
        console.error("Error fetching advertisements:", error);
        toast.error("Failed to load advertisements");
        setLoading(false);
      }
    };
    fetchAdvertisements();
  }, []);



  useEffect(() => {
    const fetchRunningAdvertisements = async () => {
      try {
        setLoading(true);

        // Call both APIs in parallel
        const runningAdsResponse = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/admin/get/all/running/ads`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        console.log("Fetched Running Advertisements Data:", runningAdsResponse);

        // Handle data shape differences

        const runningAdsData = runningAdsResponse.data.data || runningAdsResponse.data;

        setRunningAds(runningAdsData || []);
        setRunningStats({
          running: runningAdsData.filter((ad) => ad.runningAdStatus === "running").length,

        });
        setLoading(false);
      } catch (error) {
        console.error("Error fetching advertisements:", error);
        // toast.error("Failed to load advertisements");
        setLoading(false);
      }
    };
    fetchRunningAdvertisements();
  }, []);



  // Filter advertisements based on search and filters
  const filteredAdvertisements = advertisements.filter((ad) => {
    // Search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      const matchesSearch =
        (ad._id && ad._id.toLowerCase().includes(searchLower)) ||
        (ad.adState && ad.adState.toLowerCase().includes(searchLower)) ||
        (ad.adCity && ad.adCity.toLowerCase().includes(searchLower)) ||
        (ad.adType && ad.adType.toLowerCase().includes(searchLower));

      if (!matchesSearch) return false;
    }

    // Status filter
    if (statusFilter !== "all") {
      if (ad.status !== statusFilter) return false;
    }

    // User type filter
    if (userTypeFilter !== "all") {
      if (ad.userType !== userTypeFilter) return false;
    }

    return true;
  });

  // Handle card click to navigate to specific pages
  const handleCardClick = (type) => {
    switch (type) {
      case "pending":
        navigate("/admin/pendingposts");
        break;
      case "approved":
        navigate("/admin/approvedposts");
        break;
      case "accepted":
        navigate("/admin/acceptedbyreporterposts");
        break;
      case "rejected":
        navigate("/admin/rejectedposts");
        break;
      case "running":
        navigate("/admin/runningposts");
        break;
      case "completed":
        navigate("/admin/completedposts");
        break;
      default:
        break;
    }
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-700 rounded-xl md:rounded-2xl shadow-xl p-4 md:p-8 mb-6 md:mb-8 text-white relative overflow-hidden mx-2 md:mx-0">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <h1 className="text-xl md:text-3xl lg:text-4xl font-black mb-1 md:mb-2 flex items-center gap-2 md:gap-3">
                <FaChartBar className="text-blue-300 w-5 h-5 md:w-8 md:h-8" />
                Ad Dashboard
              </h1>
              <p className="text-blue-100 text-xs md:text-lg opacity-80">
                Live metrics and analytical overview
              </p>
              <div className="mt-4 flex items-center gap-2 md:gap-4">
                <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg px-3 py-1.5 md:px-4 md:py-2 border border-white/10">
                  <span className="text-[10px] md:text-sm text-blue-100 block opacity-70">Total Volume</span>
                  <div className="text-sm md:text-2xl font-black text-white">{advertisements.length}</div>
                </div>
                <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg px-3 py-1.5 md:px-4 md:py-2 border border-white/10">
                  <span className="text-[10px] md:text-sm text-blue-100 block opacity-70">Filtered View</span>
                  <div className="text-sm md:text-2xl font-black text-white">{filteredAdvertisements.length}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-6 mb-8 mt-[-20px] md:mt-0 px-2 md:px-0">
        <div
          className="bg-gradient-to-br from-yellow-500 to-amber-600 p-2 md:p-6 rounded-xl md:rounded-2xl shadow-lg text-white cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:scale-105"
          onClick={() => handleCardClick("pending")}
        >
          <div className="flex flex-col items-center text-center">
            <FaHourglassHalf className="w-5 h-5 md:w-12 md:h-12 mb-1 md:mb-3 opacity-90" />
            <p className="text-[8px] md:text-sm font-black uppercase tracking-widest opacity-90">Pending</p>
            <p className="text-sm md:text-3xl font-black mt-1">
              {loading ? "..." : stats.pending}
            </p>
          </div>
        </div>

        <div
          className="bg-gradient-to-br from-green-500 to-emerald-600 p-2 md:p-6 rounded-xl md:rounded-2xl shadow-lg text-white cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:scale-105"
          onClick={() => handleCardClick("approved")}
        >
          <div className="flex flex-col items-center text-center">
            <FaCheckCircle className="w-5 h-5 md:w-12 md:h-12 mb-1 md:mb-3 opacity-90" />
            <p className="text-[8px] md:text-sm font-black uppercase tracking-widest opacity-90">Tracking</p>
            <p className="text-sm md:text-3xl font-black mt-1">
              {loading ? "..." : stats.approved}
            </p>
          </div>
        </div>

        <div
          className="bg-gradient-to-br from-cyan-500 to-teal-600 p-2 md:p-6 rounded-xl md:rounded-2xl shadow-lg text-white cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:scale-105"
          onClick={() => handleCardClick("accepted")}
        >
          <div className="flex flex-col items-center text-center">
            <FaUsers className="w-5 h-5 md:w-12 md:h-12 mb-1 md:mb-3 opacity-90" />
            <p className="text-[8px] md:text-sm font-black uppercase tracking-widest opacity-90">Accepted</p>
            <p className="text-sm md:text-3xl font-black mt-1">
              {loading ? "..." : stats.accepted}
            </p>
          </div>
        </div>

        <div
          className="bg-gradient-to-br from-red-500 to-rose-600 p-2 md:p-6 rounded-xl md:rounded-2xl shadow-lg text-white cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:scale-105"
          onClick={() => handleCardClick("rejected")}
        >
          <div className="flex flex-col items-center text-center">
            <FaTimesCircle className="w-5 h-5 md:w-12 md:h-12 mb-1 md:mb-3 opacity-90" />
            <p className="text-[8px] md:text-sm font-black uppercase tracking-widest opacity-90">Rejected</p>
            <p className="text-sm md:text-3xl font-black mt-1">
              {loading ? "..." : stats.rejected}
            </p>
          </div>
        </div>

        <div
          className="bg-gradient-to-br from-blue-500 to-indigo-600 p-2 md:p-6 rounded-xl md:rounded-2xl shadow-lg text-white cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:scale-105"
          onClick={() => handleCardClick("running")}
        >
          <div className="flex flex-col items-center text-center">
            <FaPlay className="w-5 h-5 md:w-12 md:h-12 mb-1 md:mb-3 opacity-90" />
            <p className="text-[8px] md:text-sm font-black uppercase tracking-widest opacity-90">Running</p>
            <p className="text-sm md:text-3xl font-black mt-1">
              {loading ? "..." : runningStats.running}
            </p>
          </div>
        </div>

        <div
          className="bg-gradient-to-br from-purple-500 to-violet-600 p-2 md:p-6 rounded-xl md:rounded-2xl shadow-lg text-white cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:scale-105"
          onClick={() => handleCardClick("completed")}
        >
          <div className="flex flex-col items-center text-center">
            <FaCheck className="w-5 h-5 md:w-12 md:h-12 mb-1 md:mb-3 opacity-90" />
            <p className="text-[8px] md:text-sm font-black uppercase tracking-widest opacity-90">Done</p>
            <p className="text-sm md:text-3xl font-black mt-1">
              {loading ? "..." : stats.completed}
            </p>
          </div>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-4 md:p-6 mb-6 md:mb-8 border border-gray-100 mx-2 md:mx-0">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex-1">
            <div className="relative">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 bg-gray-50 focus:bg-white"
                placeholder="Search by Ad ID, State, City, or Type..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className="flex-1 md:flex-none border border-gray-200 rounded-lg md:rounded-xl px-3 py-2 md:px-4 md:py-3 text-xs md:text-sm font-bold focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <option value="all">Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="running">Running</option>
              <option value="completed">Completed</option>
            </select>
            <select
              className="flex-1 md:flex-none border border-gray-200 rounded-lg md:rounded-xl px-3 py-2 md:px-4 md:py-3 text-xs md:text-sm font-bold focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
              value={userTypeFilter}
              onChange={e => setUserTypeFilter(e.target.value)}
            >
              <option value="all">Role Types</option>
              <option value="reporter">Reporter</option>
              <option value="influencer">Influencer</option>
            </select>
            <div className="bg-blue-50 text-blue-600 rounded-lg px-3 py-2 text-[10px] md:text-xs font-black uppercase tracking-widest hidden md:block">
              Page {currentPage} of {Math.ceil(filteredAdvertisements.length / rowsPerPage)}
            </div>
            <div className="bg-emerald-50 text-emerald-600 rounded-lg px-3 py-2 text-[10px] md:text-xs font-black uppercase tracking-widest">
              {filteredAdvertisements.length} Found
            </div>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl text-[10px] md:text-sm font-black uppercase tracking-widest shadow-md hover:shadow-xl transition-all duration-300 flex items-center gap-2 ml-auto"
            >
              <FaDownload className="w-3 h-3 md:w-4 md:h-4" />
              Sync
            </button>
          </div>
        </div>
      </div>

      {/* All Ads Table */}
      {!loading && advertisements.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-100">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              <FaAd className="w-5 h-5 text-blue-600" />
              All Advertisements
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <FaCalendar className="inline w-4 h-4 mr-2" />
                    Start Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <FaUser className="inline w-4 h-4 mr-2" />
                    Ad ID
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <FaCalendar className="inline w-4 h-4 mr-2" />
                    End Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <FaMoneyBillWave className="inline w-4 h-4 mr-2" />
                    Budget
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <FaFilter className="inline w-4 h-4 mr-2" />
                    Ad Length
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <FaEye className="inline w-4 h-4 mr-2" />
                    Required Views
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <FaUsers className="inline w-4 h-4 mr-2" />
                    Required Reporter
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <FaUser className="inline w-4 h-4 mr-2" />
                    Target User Type
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <FaClock className="inline w-4 h-4 mr-2" />
                    Created At
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <FaCheckCircle className="inline w-4 h-4 mr-2" />
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(() => {
                  const totalPages = Math.ceil(filteredAdvertisements.length / rowsPerPage);
                  const paginatedAds = filteredAdvertisements.slice(
                    (currentPage - 1) * rowsPerPage,
                    currentPage * rowsPerPage
                  );
                  return paginatedAds.map((ad) => (
                    <tr key={ad._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                        {ad.startDate || "-"}
                      </td>
                      <td className="px-6 py-4">
                        <div className="bg-blue-100 text-blue-800 rounded-lg px-3 py-1 font-mono text-sm">
                          {ad._id}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                        {ad.endDate || "-"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 font-semibold">
                        ₹{ad.totalCost || 0}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {ad.adLength || "-"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {ad.requiredViews || "-"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {ad.requiredReporter || "-"}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${ad.userType === 'influencer'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-blue-100 text-blue-800'
                          }`}>
                          {ad.userType === 'influencer' ? '🎯 Influencer' : '📰 Reporter'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <div>
                          <div className="font-medium">{new Date(ad.createdAt).toLocaleDateString('en-IN', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}</div>
                          <div className="text-gray-400">{new Date(ad.createdAt).toLocaleTimeString('en-IN', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true,
                          })}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${ad.status === "approved"
                          ? "bg-green-100 text-green-800"
                          : ad.status === "rejected"
                            ? "bg-red-100 text-red-800"
                            : ad.status === "running"
                              ? "bg-blue-100 text-blue-800"
                              : ad.status === "completed"
                                ? "bg-purple-100 text-purple-800"
                                : "bg-yellow-100 text-yellow-800"
                          }`}>
                          {ad.status === "pending" ? "Under Review" : ad.status}
                        </span>
                      </td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Pagination */}
      {(() => {
        const totalPages = Math.ceil(filteredAdvertisements.length / rowsPerPage);
        return totalPages > 1 ? (
          <div className="flex justify-center mt-8">
            <nav className="inline-flex items-center space-x-2 bg-white rounded-2xl shadow-lg p-2 border border-gray-100">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${currentPage === 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-blue-600 hover:bg-blue-50 hover:text-blue-700'
                  }`}
              >
                Previous
              </button>

              {[...Array(totalPages)].map((_, idx) => (
                <button
                  key={idx + 1}
                  onClick={() => setCurrentPage(idx + 1)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${currentPage === idx + 1
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
                    }`}
                  aria-current={currentPage === idx + 1 ? 'page' : undefined}
                >
                  {idx + 1}
                </button>
              ))}

              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${currentPage === totalPages
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-blue-600 hover:bg-blue-50 hover:text-blue-700'
                  }`}
              >
                Next
              </button>
            </nav>
          </div>
        ) : null;
      })()}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <div className="flex flex-col items-center p-8 bg-white rounded-2xl shadow-2xl border border-blue-200">
            {/* Custom Tailwind Spinner */}
            <div className="relative mb-4 w-16 h-16 grid place-items-center">
              {/* Outer ring */}
              <span className="absolute inset-0 rounded-full border-4 border-transparent border-r-blue-500 animate-spin" style={{ borderWidth: "4px" }} />
              {/* Middle ring */}
              <span className="absolute inset-0 m-1 rounded-full border-4 border-transparent border-r-blue-500 animate-spin-slow" style={{ borderWidth: "4px", animationDuration: "2s" }} />
              {/* Inner ring */}
              <span className="absolute inset-0 m-2 rounded-full border-4 border-transparent border-r-blue-500 animate-spin-slower" style={{ borderWidth: "4px", animationDuration: "3s" }} />
            </div>
            <span className="text-lg font-semibold text-blue-700 tracking-wide">
              Loading Dashboard...
            </span>
            <span className="text-sm text-gray-500 mt-2">
              Please wait while we fetch the data.
            </span>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && advertisements.length === 0 && (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-100">
          <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center">
            <FaAd className="w-12 h-12 text-blue-500" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-3">
            No Advertisements Found
          </h3>
          <p className="text-gray-500 text-lg mb-6">
            You don't have any advertisements at the moment.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
          >
            Refresh
          </button>
        </div>
      )}
    </>
  );
};

export default AdminAdvertisementStatus;
